import React from "react"
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps"
import { v4 as uuidv4 } from "uuid"
import { useGoogleMapsEffect } from "../lib/hooks/useGoogleMapsEffect"
import { Polygon, MultiPolygonData } from "../lib/helpers/map"
import { ZoneEntity } from "../lib/types/zone"

const DRAWED_POLYGON_OPTIONS = {
  fillColor: "#FFA743",
  fillOpacity: 0.3,
  strokeColor: "#FFA743",
  strokeWeight: 1,
  strokeOpacity: 1,
  draggable: true,
  editable: true,
  zIndex: 9099999,
}

interface GoogleMapsDrawingPolygonManagerProps {
  mapId: string
  isCreating: boolean
  onCreateFinish: (polygonId: string, polygonCoords: Polygon) => void
  onEditFinish: (polygon?: MultiPolygonData) => void
  editableZone: ZoneEntity | null
}

const DrawingPolygonManager: React.FC<GoogleMapsDrawingPolygonManagerProps> = ({
  mapId,
  isCreating,
  onCreateFinish,
  onEditFinish,
  editableZone,
}) => {
  const gMap = useMap(mapId)

  const drawingLibrary = useMapsLibrary("drawing")

  // drawed shape
  const [drawedGooglePolygon, setDrawedGooglePolygon] = React.useState<google.maps.Polygon>()

  // edited shape
  const [editedGooglePolygon, setEditedGooglePolygon] = React.useState<google.maps.Polygon>()

  const polygonId = React.useRef<string>("")
  const isDragging = React.useRef<boolean>(false)

  const getCoordsArrayFromGooglePolygon = React.useCallback((polygon: google.maps.Polygon) => {
    // get an array of lat/lng
    const pathsArray = polygon.getPath().getArray()
    // loop thru the MVCArray<LatLng> and get the values
    const polygonCoordsArray = pathsArray.map((path) => [path.lng(), path.lat()]) as Polygon
    // repeat last node at the end -> workaround for simulating Mapbox format (API accepts only this)
    polygonCoordsArray.push(polygonCoordsArray[0])
    return polygonCoordsArray
  }, [])

  const updateNewPolygon = React.useCallback(
    (newPolygon: google.maps.Polygon) => {
      // set to state variable so we can delete it when creation is finished or canceled
      setDrawedGooglePolygon(newPolygon)
      // convert format
      const polygonCoordsArray = getCoordsArrayFromGooglePolygon(newPolygon)
      // invoke callback
      onCreateFinish(polygonId.current, polygonCoordsArray)
    },
    [setDrawedGooglePolygon, getCoordsArrayFromGooglePolygon, onCreateFinish],
  )

  const updateEditedPolygon = React.useCallback(
    (editedPolygon: google.maps.Polygon) => {
      // set to state variable so we can delete it when editing is finished or canceled
      setEditedGooglePolygon(editedPolygon)
      // convert format
      const polygonCoordsArray = getCoordsArrayFromGooglePolygon(editedPolygon)
      // invoke callback
      onEditFinish([[polygonCoordsArray]])
    },
    [setEditedGooglePolygon, getCoordsArrayFromGooglePolygon, onEditFinish],
  )

  const handleOverlayComplete = React.useCallback(
    (e: any, manager: google.maps.drawing.DrawingManager) => {
      // get new shape from event
      const newPolygon = e.overlay as google.maps.Polygon

      // create polygon ID
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      polygonId.current = uuidv4()

      // set listeners to newely created shape to listen to drags and edits
      // dragstart -> set variable to avoid useless multiple updates
      newPolygon.addListener("dragstart", () => (isDragging.current = true))

      // listen when user stopped dragging shape -> update polygon
      newPolygon.addListener("dragend", (e: any) => {
        isDragging.current = false
        updateNewPolygon(newPolygon)
      })

      // for each path listen when nodes are dragged -> update polygon
      const paths = newPolygon.getPaths()
      paths.forEach((path) =>
        path.addListener("set_at", () => {
          // update only if is not dragging (otherwise too many events)
          if (!isDragging.current) {
            updateNewPolygon(newPolygon)
          }
        }),
      )

      updateNewPolygon(newPolygon)

      // stop drawing mode
      manager.setDrawingMode(null)
    },
    [isDragging, updateNewPolygon],
  )

  // init drawing manager
  const drawingManager = React.useMemo(() => {
    if (!drawingLibrary || !gMap) {
      return
    }

    const manager = new drawingLibrary.DrawingManager()

    manager.setOptions({
      drawingControl: false,
      polygonOptions: DRAWED_POLYGON_OPTIONS,
    })

    manager.setMap(gMap)

    google.maps.event.addListener(manager, "overlaycomplete", (e: any) => handleOverlayComplete(e, manager))

    return manager
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gMap, drawingLibrary])

  // start and stop drawing mode
  useGoogleMapsEffect(
    mapId,
    () => {
      if (!drawingManager) {
        return
      }

      isCreating
        ? drawingManager.setDrawingMode(google.maps.drawing.OverlayType.POLYGON)
        : drawingManager.setDrawingMode(null)

      return () => drawingManager.setDrawingMode(null)
    },
    [isCreating, drawingManager],
  )

  // clear drawed shape when stop creating
  React.useEffect(() => {
    if (isCreating || !drawedGooglePolygon) {
      return
    }

    // clear
    drawedGooglePolygon.setEditable(false)
    drawedGooglePolygon.setMap(null)
    setDrawedGooglePolygon(undefined)
  }, [isCreating, drawedGooglePolygon])

  // editing zone
  useGoogleMapsEffect(
    mapId,
    () => {
      if (editableZone && editableZone.polygon.coordinates.length > 0) {
        // extract paths array from selected zone polygon
        const paths = editableZone.polygon.coordinates[0][0].map((path) => ({
          lng: path[0],
          lat: path[1],
        }))

        // create a new editable polygon equal to the selected one
        const editablePolygon = new google.maps.Polygon({ paths, ...DRAWED_POLYGON_OPTIONS })

        // set listeners to new editable created shape to listen to drags and edits
        // dragstart -> set variable to avoid useless multiple updates
        editablePolygon.addListener("dragstart", () => (isDragging.current = true))

        // listen when user stopped dragging shape -> update polygon
        editablePolygon.addListener("dragend", () => {
          isDragging.current = false
          updateEditedPolygon(editablePolygon)
        })

        // for each path listen when nodes are dragged -> update polygon
        const polygonPaths = editablePolygon.getPaths()
        polygonPaths.forEach((path) =>
          path.addListener("set_at", () => {
            // update only if is not dragging (otherwise too many events)
            if (!isDragging.current) {
              updateEditedPolygon(editablePolygon)
            }
          }),
        )

        updateEditedPolygon(editablePolygon)

        // add new editable polygon to map
        editablePolygon.setMap(gMap)
      }
    },
    [editableZone],
  )

  React.useEffect(() => {
    if (!editableZone && editedGooglePolygon) {
      // cancel edited polygon if any
      editedGooglePolygon?.setMap(null)
    }
  }, [editableZone, editedGooglePolygon])

  return null
}

export default DrawingPolygonManager
